/**
 * Browser-storage hygiene for the payroll controls preview pages (CO-856).
 *
 * On a company with generated payroll those pages read the company's REAL employees, payroll rows and bank details
 * (views/Previews/PayrollControls/data/realSource.ts). Bank details (IBAN, labour card number) and payment-file text
 * may not stay at rest in the browser; the workflow state below still does, with the pay data it holds:
 *
 *  - `mowazaf.payrollControls.cache.*`  — the former 12 h localStorage cache of per-employee records (IBAN, labour
 *    card number, name, email) and loan / salary-deduction rows. Nothing writes it any more (the reads are kept in
 *    memory only); every key under the prefix is REMOVED here, so a browser that used the pages before is cleaned.
 *  - `mowazaf.preview.payrollControls.v1.<companyId>.real` — the presenter's workflow state on a real-data run
 *    (approvals, releases, reconciliation, and with them the company's loan and salary-advance rows and any imported
 *    input rows, keyed by employee user id, plus run-level totals). It is KEPT across sign-out, with no expiry
 *    (owner default, 2026-10-13: it exists nowhere else); only the text of frozen payment files (SIF and bank
 *    transfer file: IBANs and net pay) is stripped from it. The files are rebuilt from the server's data when a
 *    page needs them.
 *
 * 🔴 This file is imported by the sign-out code in the MAIN bundle (utils/AuthService.ts,
 * store/redux/actions/userActionCreator.ts). It must never import anything from `views/Previews`: those pages are
 * lazy chunks. The preview bundle imports THIS file, never the other way round; it hands its in-memory caches over
 * through `registerPayrollPreviewMemory`.
 *
 * Every storage access is wrapped: with storage blocked these are no-ops, and sign-out never fails on them.
 */

/** Prefix of the retired per-employee cache, every version (`…cache.v2.details.<companyId>.<userId>`, …). */
export const PAYROLL_PREVIEW_CACHE_PREFIX = 'mowazaf.payrollControls.cache.';
/** Prefix of the preview workflow state (`<prefix><companyId>` on fixtures, `<prefix><companyId>.real` on real data). */
export const PAYROLL_PREVIEW_STATE_PREFIX = 'mowazaf.preview.payrollControls.v1.';
/** Suffix of the workflow-state key of a company that runs on its real payroll data. */
export const PAYROLL_PREVIEW_REAL_SUFFIX = '.real';

const memoryClearers = new Set<() => void>();

/**
 * Called once by the preview bundle (data/realSource.ts) with a function that empties its in-memory caches, so a
 * sign-out drops them even before the page navigates away. Returns an unregister function.
 */
export function registerPayrollPreviewMemory(clear: () => void): () => void {
  memoryClearers.add(clear);
  return () => {
    memoryClearers.delete(clear);
  };
}

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null; // access denied (blocked storage)
  }
}

function keysWithPrefix(store: Storage, prefix: string): string[] {
  const keys: string[] = [];
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i);
    if (key && key.startsWith(prefix)) keys.push(key);
  }
  return keys;
}

/** Removes every key of the retired per-employee cache. Returns how many were removed. */
export function removePayrollPreviewCacheKeys(): number {
  const store = storage();
  if (!store) return 0;
  let removed = 0;
  try {
    // Collected first: removing while walking by index would skip keys.
    for (const key of keysWithPrefix(store, PAYROLL_PREVIEW_CACHE_PREFIX)) {
      store.removeItem(key);
      removed += 1;
    }
  } catch {
    /* storage blocked: nothing can be at rest there either */
  }
  return removed;
}

/**
 * A copy of a frozen-file list without the files' text. Name, row count, total, SHA-256 and freeze stamp are kept:
 * they are what the pages list as proof; the text is what holds IBANs and net pay. Same array back when no file
 * carries text. Structural on purpose (this file cannot import the preview's types).
 */
export function withoutFrozenFileText<T extends { content?: string }>(files: readonly T[]): T[] {
  if (!files.some((f) => f.content !== undefined)) return files as T[];
  return files.map((f) => {
    if (f.content === undefined) return f;
    const copy: { content?: string } = { ...f };
    delete copy.content;
    return copy as T;
  });
}

interface StoredRunLike {
  frozenFiles?: Array<{ content?: string }>;
}

/**
 * Strips the text of frozen payment files from every stored real-data workflow state in this browser, keeping the
 * rest of the state. Returns how many keys were rewritten. A value that cannot be parsed is left alone: the preview
 * store ignores it on load and overwrites it on the next action.
 */
export function scrubPayrollPreviewRealState(): number {
  const store = storage();
  if (!store) return 0;
  let rewritten = 0;
  try {
    const keys = keysWithPrefix(store, PAYROLL_PREVIEW_STATE_PREFIX).filter((k) => k.endsWith(PAYROLL_PREVIEW_REAL_SUFFIX));
    for (const key of keys) {
      try {
        const raw = store.getItem(key);
        if (!raw) continue;
        const parsed = JSON.parse(raw) as { runs?: StoredRunLike[] } | null;
        if (!parsed || !Array.isArray(parsed.runs)) continue;
        let changed = false;
        const runs = parsed.runs.map((run) => {
          if (!run || !Array.isArray(run.frozenFiles)) return run;
          const files = withoutFrozenFileText(run.frozenFiles);
          if (files === run.frozenFiles) return run;
          changed = true;
          return { ...run, frozenFiles: files };
        });
        if (!changed) continue;
        store.setItem(key, JSON.stringify({ ...parsed, runs }));
        rewritten += 1;
      } catch {
        /* one unreadable key must not stop the others */
      }
    }
  } catch {
    /* storage blocked */
  }
  return rewritten;
}

/**
 * Sign-out cleanup, and the one-time cleanup the preview bundle runs when it loads: removes the retired cache keys,
 * strips frozen-file text from the real-data workflow state, and empties the preview's in-memory caches. Never
 * throws. The workflow state itself (fixtures and real) is deliberately left in place.
 */
export function clearPayrollPreviewStorage(): void {
  removePayrollPreviewCacheKeys();
  scrubPayrollPreviewRealState();
  memoryClearers.forEach((clear) => {
    try {
      clear();
    } catch {
      /* a failing clearer must not block sign-out */
    }
  });
}
